import React, { useMemo } from "react";
import Link from "@mui/material/Link";

type Segment = { type: "text" | "url"; value: string };

function splitTextAndUrls(text: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;
  const re = /https?:\/\/[^\s<]+/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({
        type: "text",
        value: text.slice(lastIndex, m.index),
      });
    }
    let url = m[0];
    url = url.replace(/[.,;:!?)]+$/g, "");
    segments.push({ type: "url", value: url });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }
  return segments;
}

function linkLabelForUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = (u.pathname || "").toLowerCase();
    if (path.includes("calculationerror")) {
      return "Calculation view";
    }
  } catch {
    // ignore
  }
  return "Open link";
}

export interface TicketMessageBodyProps {
  text: string;
  linkColor?: string;
}

/**
 * Renders stored message text for display only. URLs become short labeled links;
 * the underlying `text` / API payload is unchanged.
 */
export function TicketMessageBody({ text, linkColor }: TicketMessageBodyProps) {
  const segments = useMemo(() => splitTextAndUrls(text || ""), [text]);

  const bodySx = {
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
  };

  return (
    <span style={bodySx}>
      {segments.map((seg, i) =>
        seg.type === "text" ? (
          <span key={i}>{seg.value}</span>
        ) : (
          <Link
            key={i}
            href={seg.value}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              fontWeight: 500,
              ...(linkColor ? { color: linkColor } : {}),
            }}
          >
            {linkLabelForUrl(seg.value)}
          </Link>
        ),
      )}
    </span>
  );
}
